import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast, SwipableDialog } from '@tix-ticketing/ui-components';
import { ChevronDownIcon } from '@tix-ticketing/ui-icons/outline';
import type { CountryCode } from 'libphonenumber-js';
import { useId, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { postApiWaitingListByEventIdSignup } from 'api/generated';
import { getApiAppsettingsAttributesOptions } from 'api/generated/@tanstack/react-query.gen';
import { PhoneInput } from 'components/common/forms/phone-input';
import { toastError } from 'lib/helpers/error-toast-helpers';
import { useDefaultPhoneCountry } from 'lib/hooks/use-default-phone-country';
import { useServerTimezone } from 'lib/hooks/use-server-timezone';
type FormDTO = {
  fullName: string;
  email: string;
  phoneNumber: string;
  tickets: number;
  note: string;
};
export const WaitingListDialog = ({
  open,
  onClose,
  eventId,
  eventName,
  eventStartDate,
  maxTickets
}: {
  open: boolean;
  onClose: () => void;
  eventId: number;
  eventName?: string | null;
  eventStartDate?: string | null;
  maxTickets?: number | null;
}) => {
  const intl = useIntl();
  const {
    formatDate,
    formatTime
  } = useServerTimezone();
  const defaultCountry = useDefaultPhoneCountry();
  const ticketsLabelId = useId();

  // Only a country the user actually picked is held in state; everything else
  // falls back to the current default, so a default that resolves after mount
  // is still reflected here without clobbering a manual selection.
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>();
  const country = selectedCountry ?? defaultCountry;

  // Countries carry the numeric CountryId the backend expects; the phone
  // dropdown's ISO region is mapped back to that id on submit. Fetched only
  // once a dialog opens (shared/cached across cards by query key).
  const attributesQuery = useQuery({
    ...getApiAppsettingsAttributesOptions(),
    enabled: open
  });
  const ticketBound = Math.max(maxTickets ?? 1, 1);
  const requiredMsg = (field: string) => intl.formatMessage({
    id: "validation.fieldRequired",
    defaultMessage: [{
      "type": 1,
      "value": "field"
    }, {
      "type": 0,
      "value": " is required"
    }]
  }, {
    field
  });
  const maxLengthMsg = (field: string, max: number) => intl.formatMessage({
    id: "validation.fieldMaxLength",
    defaultMessage: [{
      "type": 1,
      "value": "field"
    }, {
      "type": 0,
      "value": " must be at most "
    }, {
      "type": 1,
      "value": "max"
    }, {
      "type": 0,
      "value": " characters"
    }]
  }, {
    field,
    max
  });
  const invalidEmailMsg = intl.formatMessage({
    id: "validation.invalidEmail",
    defaultMessage: [{
      "type": 0,
      "value": "Please enter a valid email address"
    }]
  });
  const ticketsMaxMsg = intl.formatMessage({
    id: "waitingList.ticketsMaxError",
    defaultMessage: [{
      "type": 0,
      "value": "You can select a maximum of "
    }, {
      "type": 6,
      "value": "max",
      "options": {
        "one": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " ticket"
          }]
        },
        "other": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " tickets"
          }]
        }
      },
      "offset": 0,
      "pluralType": "cardinal"
    }]
  }, {
    max: ticketBound
  });
  const fullNameLabel = intl.formatMessage({
    id: "waitingList.fullName",
    defaultMessage: [{
      "type": 0,
      "value": "Full name"
    }]
  });
  const emailLabel = intl.formatMessage({
    id: "auth.emailAddress",
    defaultMessage: [{
      "type": 0,
      "value": "Email address"
    }]
  });
  const noteLabel = intl.formatMessage({
    id: "waitingList.note",
    defaultMessage: [{
      "type": 0,
      "value": "Optional note"
    }]
  });
  const ticketsLabel = intl.formatMessage({
    id: "waitingList.tickets",
    defaultMessage: [{
      "type": 0,
      "value": "Tickets"
    }]
  });
  const schema = yup.object().shape({
    fullName: yup.string().trim().required(requiredMsg(fullNameLabel)).max(512, maxLengthMsg(fullNameLabel, 512)),
    email: yup.string().trim().required(requiredMsg(emailLabel)).email(invalidEmailMsg).max(512, maxLengthMsg(emailLabel, 512)),
    // Optional to fill in, but always present — the form supplies '' as the
    // default, so `defined()` keeps the schema's shape in step with FormDTO.
    phoneNumber: yup.string().defined().max(16, maxLengthMsg(intl.formatMessage({
      id: "waitingList.phone",
      defaultMessage: [{
        "type": 0,
        "value": "Phone number"
      }]
    }), 16)),
    tickets: yup.number().required(requiredMsg(ticketsLabel)).max(ticketBound, ticketsMaxMsg),
    note: yup.string().defined().max(512, maxLengthMsg(noteLabel, 512))
  }).required();
  const form = useForm<FormDTO>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      fullName: '',
      email: '',
      phoneNumber: '',
      tickets: 1,
      note: ''
    }
  });
  const {
    register,
    handleSubmit,
    control,
    formState: {
      errors,
      isValid
    },
    reset
  } = form;
  const handleClose = () => {
    reset();
    setSelectedCountry(undefined);
    onClose();
  };

  // Omit the id when the region can't be mapped (list not loaded yet or no
  // match) rather than sending a bogus 0.
  const resolveCountryId = (): number | undefined => attributesQuery.data?.countries?.find(c => c.code?.toUpperCase() === country)?.id;
  const signupMutation = useMutation({
    mutationFn: (body: FormDTO) => postApiWaitingListByEventIdSignup({
      path: {
        eventId
      },
      body: {
        fullName: body.fullName.trim(),
        email: body.email.trim(),
        phoneNumber: body.phoneNumber || null,
        countryId: resolveCountryId(),
        tickets: body.tickets,
        note: body.note.trim() || null
      }
    }),
    onSuccess: (data: any) => {
      if (data.status !== 200) {
        toastError(data.response?.data);
        return;
      }
      showToast({
        title: intl.formatMessage({
          id: "waitingList.successTitle",
          defaultMessage: [{
            "type": 0,
            "value": "Check your inbox"
          }]
        }),
        message: intl.formatMessage({
          id: "waitingList.successMessage",
          defaultMessage: [{
            "type": 0,
            "value": "We've sent you an email. Please check your inbox to confirm waiting list signup."
          }]
        }),
        severity: 'success'
      });
      handleClose();
    }
  });
  const onSubmit: SubmitHandler<FormDTO> = data => {
    signupMutation.mutate(data);
  };
  return <SwipableDialog open={open} onClose={handleClose} headerText={intl.formatMessage({
    id: "waitingList.title",
    defaultMessage: [{
      "type": 0,
      "value": "Waiting list"
    }]
  })} subtitleText={intl.formatMessage({
    id: "waitingList.description",
    defaultMessage: [{
      "type": 0,
      "value": "Please sign up if you want to register on a waiting list for: "
    }, {
      "type": 1,
      "value": "name"
    }, {
      "type": 0,
      "value": " on "
    }, {
      "type": 1,
      "value": "date"
    }, {
      "type": 0,
      "value": " at "
    }, {
      "type": 1,
      "value": "time"
    }]
  }, {
    name: eventName ?? '',
    date: eventStartDate ? formatDate(eventStartDate, {
      dateStyle: 'long'
    }) : '',
    time: eventStartDate ? formatTime(eventStartDate, {
      timeStyle: 'short'
    }) : ''
  })} primaryActionButton={<Button fullWidth size="large" type="submit" form="waiting-list-form" variant="contained" loading={signupMutation.isPending} disabled={!isValid}>
          <FormattedMessage id="waitingList.submit" defaultMessage={[{
      "type": 0,
      "value": "Sign up"
    }]} />
        </Button>}>
      <form id="waiting-list-form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={5}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <TextField label={fullNameLabel} {...register('fullName')} error={!!errors.fullName} helperText={errors.fullName?.message ?? ''} />
            </FormControl>
            <FormControl fullWidth>
              <TextField label={emailLabel} type="email" {...register('email')} error={!!errors.email} helperText={errors.email?.message ?? ''} />
            </FormControl>
            <Controller control={control} name="phoneNumber" render={({
            field
          }) => <PhoneInput value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} onCountryChange={setSelectedCountry} defaultCountry={defaultCountry} label={intl.formatMessage({
            id: "waitingList.phone",
            defaultMessage: [{
              "type": 0,
              "value": "Phone number"
            }]
          })} countryLabel={intl.formatMessage({
            id: "common.country",
            defaultMessage: [{
              "type": 0,
              "value": "Country"
            }]
          })} error={!!errors.phoneNumber} helperText={errors.phoneNumber?.message ?? ''} />} />
          </Stack>
          <Stack spacing={2}>
            <Controller control={control} name="tickets" render={({
            field
          }) => <FormControl sx={{
            width: 120,
            flexShrink: 0
          }} error={!!errors.tickets}>
                  <InputLabel id={ticketsLabelId}>{ticketsLabel}</InputLabel>
                  <Select labelId={ticketsLabelId} label={ticketsLabel} IconComponent={ChevronDownIcon} value={field.value} onChange={e => field.onChange(Number(e.target.value))}>
                    {Array.from({
                length: ticketBound
              }, (_, i) => i + 1).map(n => <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>)}
                  </Select>
                  {!!errors.tickets && <FormHelperText>{errors.tickets.message}</FormHelperText>}
                </FormControl>} />
            <FormControl fullWidth>
              <TextField label={noteLabel} multiline minRows={2} maxRows={5} {...register('note')} error={!!errors.note} helperText={errors.note?.message ?? ''} />
            </FormControl>
          </Stack>
        </Stack>
      </form>
    </SwipableDialog>;
};